import React, { useEffect } from 'react';
import { Modal, Form, Input, DatePicker, Button, InputNumber, Row, Col } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './Dashboard.scss';

// Custom component to handle input with unit suffix
// This ensures Form.Item passes value and onChange correctly to the inner input
const InputWithUnit = ({ value, onChange, children, unit }) => (
  <div className="inputWithUnit">
    <div className="inputWrapper">
      {React.cloneElement(children, { value, onChange })}
    </div>
    <div className="unitLabel">{unit}</div>
  </div>
);

const UpdateSiteParametersModal = ({ open, siteInfo = {}, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const estimatedSolarSavingsRatio = Form.useWatch('estimatedSolarSavingsRatio', form);

  useEffect(() => {
    form.setFieldsValue({
      stationId: siteInfo.stationId,
      source: siteInfo.source,
      installer: siteInfo.installer,
      stationName: siteInfo.stationName,
      userName: siteInfo.userName,
      addr: siteInfo.addr,
      longitudeLatitude: siteInfo.longitude && siteInfo.latitude ? `${siteInfo.longitude},${siteInfo.latitude}` : '',
      panelTiltDeg: siteInfo.panelTiltDeg,
      panelAzimuth: siteInfo.azimuth,
      locationId: siteInfo.locationId,
      capacity: siteInfo.capacity,
      commercialOperationDate: siteInfo.commercialOperationDate ? dayjs(siteInfo.commercialOperationDate) : undefined,
      initialPerformanceRatio: siteInfo.initialPerformanceRatio,
      decayFactor: siteInfo.decayFactor,
      co2EmissionSavedRatio: siteInfo.co2EmissionSavedRatio,
      standardCoalSavedRatio: siteInfo.standardCoalSavedRatio,
      treePlantingRatio: siteInfo.treePlantingRatio,
      equiGreenCertRatio: siteInfo.equiGreenCertRatio,
      estimatedSolarEnergyUnitPrice: siteInfo.estimatedSolarEnergyUnitPrice,
      estimatedSolarSavingsRatio: siteInfo.estimatedSolarSavingsRatio,
    });
  }, [siteInfo, form]);

  const handleFinish = (values) => {
    if (values.commercialOperationDate) {
      values.commercialOperationDate = values.commercialOperationDate.format('YYYY-MM-DD');
    }
    const { longitudeLatitude } = values;
    if (longitudeLatitude) {
      const [lng, lat] = longitudeLatitude.split(',');
      values.longitude = lng.trim();
      values.latitude = lat.trim();
    }
    if (typeof values.locationId === 'string') {
      values.locationId = values.locationId.trim();
      if (!values.locationId.length) {
        delete values.locationId;
      }
    }
    delete values.longitudeLatitude;
    values.stationId = siteInfo.stationId; // Ensure stationId is included
    if (values.panelAzimuth !== undefined) {
      if (values.panelAzimuth === null || values.panelAzimuth === '') {
        delete values.azimuth;
      } else {
        values.azimuth = values.panelAzimuth;
      }
      delete values.panelAzimuth;
    }
    onSubmit(values);
  };

  return (
    <Modal
      title={`Update Site Parameters`}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="Submit"
      width={1000}
      className="updateSiteModal"
      wrapClassName="updateSiteModalWrap"
      centered
      footer={null}
      closable={false}
    >
      <Form layout="horizontal" form={form} onFinish={handleFinish}>
        <div className="modalSection siteInfoSection">
          <h3>{siteInfo.stationName}</h3>
          <p className="address">{siteInfo.addr}</p>
          <Row gutter={[24, 12]}>
            <Col span={24}>
              <Form.Item
                label="Longitude, Latitude"
                name="longitudeLatitude"
                className="formRow fullWidthInput"
              >
                <Input placeholder="0.00000,0.00000" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Panel Tilt Degree"
                name="panelTiltDeg"
                className="formRow"
              >
                <InputWithUnit unit="°">
                  <InputNumber step={0.1} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Panel Azimuth"
                name="panelAzimuth"
                className="formRow"
              >
                <InputWithUnit unit="°">
                  <InputNumber step={1} max={360} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Location ID"
                name="locationId"
                className="formRow"
              >
                <Input placeholder="QWeather location ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Capacity"
                name="capacity"
                className="formRow"
              >
                <InputWithUnit unit="kWp">
                  <InputNumber
                    step={0.01}
                    style={{ width: '100%' }}
                    formatter={value =>
                      value !== undefined
                        ? `${value}`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        : ''
                    }
                    parser={value =>
                      value ? value.replace(/\,/g, '') : ''
                    }
                  />
                </InputWithUnit>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="modalSection performanceSection">
          <h3>Performance Ratio Parameters</h3>
          <Row gutter={[24, 12]}>
            <Col span={12}>
              <Form.Item
                label="Commercial Operation Date"
                name="commercialOperationDate"
                className="formRow dateRow"
              >
                <InputWithUnit unit="yyyy/mm/dd">
                  <DatePicker
                    format="YYYY/MM/DD"
                    className="formRowDate"
                    style={{ width: '100%' }}
                  />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Initial Performance Ratio"
                name="initialPerformanceRatio"
                className="formRow"
              >
                <InputWithUnit unit="%">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Decay Factor"
                name="decayFactor"
                className="formRow"
              >
                <InputWithUnit unit="%">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="modalSection conversionSection">
          <h3>Conversion Coefficients</h3>
          <Row gutter={[24, 12]}>
            <Col span={12}>
              <Form.Item
                label="CO2 Emission Saved Ratio"
                name="co2EmissionSavedRatio"
                className="formRow"
              >
                <InputWithUnit unit="kg/kWh">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Standard Coal Saved Ratio"
                name="standardCoalSavedRatio"
                className="formRow"
              >
                <InputWithUnit unit="kg/kWh">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Tree Planting Ratio"
                name="treePlantingRatio"
                className="formRow"
              >
                <InputWithUnit unit="tree/kWh">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Equi Green Cert Ratio"
                name="equiGreenCertRatio"
                className="formRow"
              >
                <InputWithUnit unit="Cert/kWh">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Estimated Solar Energy Unit Price"
                name="estimatedSolarEnergyUnitPrice"
                className="formRow"
              >
                <InputWithUnit unit="¥/kWh">
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Estimated Solar Savings Ratio"
                name="estimatedSolarSavingsRatio"
                className="formRow"
              >
                <InputWithUnit unit={`= ${(estimatedSolarSavingsRatio || 0) * 100}% saved`}>
                  <InputNumber step={0.01} style={{ width: '100%' }} />
                </InputWithUnit>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="modalFooter">
          <Button type="primary" htmlType="submit" block className="submitBtn">
            Submit
          </Button>
          <a className="backLink" onClick={onCancel}>
            Back
          </a>
        </div>
      </Form>
    </Modal>
  );
};

export default UpdateSiteParametersModal;
